import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  usePathToken,
  useSearchWithSecrets,
} from "../../../utils/addressSecrets";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Spinner } from "../../atoms/Spinner";
import { useQueryClient } from "react-query";
import SignaturePad from "signature_pad";
import {
  useGetSigningPageData,
  useGeneratePreview,
  useSubmitSignature,
  useDeclineSignature,
  useValidateLoginToken,
  SIGNING_AUTH_ERROR_CODE,
} from "../../../hooks/esignature";
import { PhoneValidationModal } from "../../molecules/PhoneValidationModal";
import { CharacterCounter } from "../../atoms/CharacterCounter";
import {
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineDownload,
  HiOutlineClock,
  HiOutlineBan,
  HiOutlineDocumentText,
  HiOutlinePencil,
  HiOutlinePhotograph,
  HiOutlineUpload,
} from "react-icons/hi";
import LanguageSelector from "../../atoms/LanguageSelector";
import { CustomButton } from "../../atoms/CustomButton";
import BusinessCardFooter from "../../atoms/BusinessCardFooter";
import { sanitizeEsignatureInput } from "../../../utils/inputs";
import { refusalMessageKey } from "../../../utils/refusalMessageKey";
import { toast } from "react-toastify";
import { SIGNING_REFUSALS, isFinalSigningRefusal } from "./signingRefusals";
import { isCodeSpent, refusalValues } from "../../../utils/phoneCodeRefusals";

const PdfDocumentViewer = React.lazy(
  () => import("../../atoms/PdfDocumentViewer")
);

type SignatureMethod = "type" | "draw" | "upload";

const ACCEPTED_SIGNATURE_IMAGE_TYPES = ["image/png", "image/jpeg"];
const ACCEPTED_SIGNATURE_IMAGE_EXTENSIONS = ".png,.jpg,.jpeg";
const MAX_SIGNATURE_IMAGE_SIZE_MB = 5;

const isSigningAuthError = (error: any) =>
  error?.response?.data?.code === SIGNING_AUTH_ERROR_CODE;

const downloadLinkClass =
  "inline-flex items-center justify-center gap-2 px-4 py-2 min-h-[38px] " +
  "rounded-md bg-blue-600 text-sm font-medium text-white shadow-sm " +
  "no-underline transition-colors hover:bg-blue-700";

const SigningPage: React.FC = () => {
  const {
    entityId,
    userId,
    token: tokenSegment,
  } = useParams<{
    entityId: string;
    userId: string;
    token: string;
  }>();
  const token = usePathToken(tokenSegment);
  const loginToken = useSearchWithSecrets().get("login_token");
  const { t } = useTranslation("clientView");
  const queryClient = useQueryClient();

  const [signedDocumentUrl, setSignedDocumentUrl] = useState<string | null>(
    null
  );

  const {
    data: tokenData,
    isLoading: isValidatingToken,
    isError: isTokenError,
  } = useValidateLoginToken(entityId || "", userId || "", loginToken);

  const isTokenValid = !!tokenData;

  const {
    data: pageData,
    isLoading,
    isError,
    error,
  } = useGetSigningPageData(
    isTokenValid ? entityId || "" : "",
    isTokenValid ? userId || "" : "",
    isTokenValid ? token || "" : "",
    isTokenValid ? loginToken : null
  );

  const previewMutation = useGeneratePreview();
  const submitMutation = useSubmitSignature();
  const declineMutation = useDeclineSignature();

  const [signatureMethod, setSignatureMethod] =
    useState<SignatureMethod>("type");
  const [typedName, setTypedName] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string>("");
  const [uploadError, setUploadError] = useState<string>("");
  const [consent, setConsent] = useState(false);
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [choiceValues, setChoiceValues] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [authExpired, setAuthExpired] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [codeSpent, setCodeSpent] = useState(false);
  const [phoneVerificationExpiresAt, setPhoneVerificationExpiresAt] =
    useState(0);
  const [phoneVerificationPhone, setPhoneVerificationPhone] = useState("");
  const [phoneVerificationLoading, setPhoneVerificationLoading] =
    useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const [previewError, setPreviewError] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");

  type SigningStep = "fill" | "review" | "sign";
  const [currentStep, setCurrentStep] = useState<SigningStep>("fill");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);

  useEffect(() => {
    if (currentStep !== "sign") {
      setConsent(false);
      setSubmitError("");
    }
  }, [currentStep]);

  useEffect(() => {
    if (
      signatureMethod === "draw" &&
      currentStep === "sign" &&
      canvasRef.current
    ) {
      const canvas = canvasRef.current;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d")?.scale(ratio, ratio);

      signaturePadRef.current = new SignaturePad(canvas, {
        backgroundColor: "rgba(0, 0, 0, 0)",
        penColor: "rgb(0, 0, 0)",
      });

      signaturePadRef.current.addEventListener("endStroke", () => {
        setHasDrawnSignature(true);
      });
    }

    return () => {
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
    };
  }, [signatureMethod, currentStep]);

  const handleUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setUploadError("");

    if (!file) return;

    if (!ACCEPTED_SIGNATURE_IMAGE_TYPES.includes(file.type)) {
      setUploadError(t("signing.upload.invalidType") as string);
      e.target.value = "";
      return;
    }

    if (file.size > MAX_SIGNATURE_IMAGE_SIZE_MB * 1024 * 1024) {
      setUploadError(t("signing.upload.fileTooLarge") as string);
      e.target.value = "";
      return;
    }

    setUploadedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setUploadPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const SIG_WIDTH = 400;
  const SIG_HEIGHT = 160;

  const normalizeToFixedCanvas = (
    source: HTMLCanvasElement | HTMLImageElement
  ): Promise<Blob | null> => {
    const canvas = document.createElement("canvas");
    canvas.width = SIG_WIDTH;
    canvas.height = SIG_HEIGHT;
    const ctx = canvas.getContext("2d");
    if (!ctx) return Promise.resolve(null);

    const srcW =
      source instanceof HTMLCanvasElement ? source.width : source.naturalWidth;
    const srcH =
      source instanceof HTMLCanvasElement
        ? source.height
        : source.naturalHeight;
    const scale = Math.min(SIG_WIDTH / srcW, SIG_HEIGHT / srcH, 1);
    const drawW = srcW * scale;
    const drawH = srcH * scale;
    ctx.drawImage(
      source,
      (SIG_WIDTH - drawW) / 2,
      (SIG_HEIGHT - drawH) / 2,
      drawW,
      drawH
    );

    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  };

  const getSignatureBlob = useCallback(async (): Promise<Blob | null> => {
    if (signatureMethod === "type") {
      if (!typedName.trim()) return null;
      const text = typedName.trim();
      const defaultFontSize = 96;
      const minFontSize = 56;
      const targetMaxWidth = 900;
      const padding = 16;

      if (document.fonts && document.fonts.load) {
        try {
          await document.fonts.load(`${defaultFontSize}px "Dancing Script"`);
        } catch (error) {}
      }

      const measure = document.createElement("canvas").getContext("2d");
      if (!measure) return Promise.resolve(null);
      measure.font = `${defaultFontSize}px 'Dancing Script', cursive`;
      const widthAtDefault = Math.ceil(measure.measureText(text).width);

      let fontSize = defaultFontSize;
      if (widthAtDefault > targetMaxWidth) {
        fontSize = Math.max(
          minFontSize,
          Math.floor(defaultFontSize * (targetMaxWidth / widthAtDefault))
        );
      }

      measure.font = `${fontSize}px 'Dancing Script', cursive`;
      const finalTextWidth = Math.ceil(measure.measureText(text).width);

      const canvasWidth = finalTextWidth + padding * 2;
      const canvasHeight = Math.ceil(fontSize * 1.6);

      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.fillStyle = "black";
      ctx.font = `${fontSize}px 'Dancing Script', cursive`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, canvasWidth / 2, canvasHeight / 2);

      return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    }

    if (signatureMethod === "draw") {
      if (!signaturePadRef.current || signaturePadRef.current.isEmpty())
        return null;
      if (!canvasRef.current) return null;
      return normalizeToFixedCanvas(canvasRef.current);
    }

    if (signatureMethod === "upload") {
      if (!uploadedFile) return null;
      const img = new Image();
      const url = URL.createObjectURL(uploadedFile);
      return new Promise<Blob | null>((resolve) => {
        img.onload = () => {
          URL.revokeObjectURL(url);
          normalizeToFixedCanvas(img).then(resolve);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(null);
        };
        img.src = url;
      });
    }

    return null;
  }, [signatureMethod, typedName, uploadedFile]);

  const buildFormData = async (): Promise<FormData | null> => {
    const formData = new FormData();

    if (pageData?.has_signature_field) {
      const sigBlob = await getSignatureBlob();
      if (!sigBlob) return null;
      formData.append("signature_image", sigBlob, "signature.png");
      formData.append("signature_type", signatureMethod);
      if (signatureMethod === "type") {
        formData.append("signature_text", typedName.trim());
      }
    }

    formData.append("consent_text", String(t("signing.consentText") ?? ""));

    return formData;
  };

  const refusalMessage = (error: unknown) =>
    String(
      t(
        refusalMessageKey(error, SIGNING_REFUSALS, "signing.errorGeneric"),
        refusalValues(error)
      )
    );

  const closePhoneVerification = () => {
    setShowPhoneVerification(false);
    setPendingFormData(null);
  };

  /** Whether the document is now signed. */
  const executeSubmit = async (
    formData: FormData,
    otpCode?: string
  ): Promise<boolean> => {
    setSubmitError("");
    try {
      if (otpCode) {
        formData.set("otp_code", otpCode);
      }

      const res = await submitMutation.mutateAsync({
        entity_identification: entityId || "",
        user_identification: userId || "",
        signing_token: token || "",
        login_token: loginToken,
        data: formData,
      });

      if (res?.data?.code === "RSSEOTP01") {
        const element = res?.data?.element;
        const rawExpiresAt = element?.expires_at;
        const fallbackExpiresAt = Date.now() + 300000;
        const expiresAt =
          typeof rawExpiresAt === "number" && rawExpiresAt > Date.now()
            ? rawExpiresAt
            : fallbackExpiresAt;
        setPendingFormData(formData);
        setPhoneVerificationPhone(`****${element?.phone_last4 || "****"}`);
        setPhoneVerificationExpiresAt(expiresAt);
        setCodeSpent(false);
        setShowPhoneVerification(true);
        return false;
      }

      const url: string | null =
        res?.data?.element?.signed_document_url ??
        res?.data?.signed_document_url ??
        null;
      setSignedDocumentUrl(url);
      setSubmitted(true);
      queryClient.invalidateQueries([
        "signing-page",
        entityId || "",
        userId || "",
        token || "",
      ]);
      return true;
    } catch (error: any) {
      if (isSigningAuthError(error)) {
        setAuthExpired(true);
        return false;
      }
      const message = refusalMessage(error);
      setSubmitError(message);
      if (showPhoneVerification) {
        if (isFinalSigningRefusal(error)) {
          closePhoneVerification();
        } else {
          // The phone verification modal covers the inline error.
          if (isCodeSpent(error)) setCodeSpent(true);
          toast.error(message);
        }
      }
      return false;
    }
  };

  const handlePhoneVerificationConfirm = async (code: string) => {
    if (!pendingFormData) return;
    setPhoneVerificationLoading(true);
    try {
      if (await executeSubmit(pendingFormData, code)) {
        setShowPhoneVerification(false);
        setPendingFormData(null);
      }
    } finally {
      setPhoneVerificationLoading(false);
    }
  };

  const handlePhoneVerificationResend = async () => {
    if (!pendingFormData) return;
    setPhoneVerificationLoading(true);
    try {
      const freshFormData = await buildFormData();
      if (freshFormData) {
        await executeSubmit(freshFormData);
      }
    } finally {
      setPhoneVerificationLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!areInputFieldsValid() || !areChoiceQuestionsValid()) {
      setShowValidationErrors(true);
      return;
    }

    setPreviewError("");
    try {
      const res = await previewMutation.mutateAsync({
        entity_identification: entityId || "",
        user_identification: userId || "",
        signing_token: token || "",
        login_token: loginToken,
        input_values: inputValues,
        choice_values: choiceValues,
      });

      const url = res?.data?.element?.preview_url;
      if (url) {
        setPreviewUrl(url);
        setCurrentStep("review");
      }
    } catch (error: any) {
      if (isSigningAuthError(error)) {
        setAuthExpired(true);
        return;
      }
      setPreviewError(refusalMessage(error));
    }
  };

  const handleApprove = () => {
    setCurrentStep("sign");
  };

  const handleSubmit = async () => {
    const formData = await buildFormData();
    if (!formData) return;

    await executeSubmit(formData);
  };

  const handleDecline = async () => {
    try {
      await declineMutation.mutateAsync({
        entity_identification: entityId || "",
        user_identification: userId || "",
        signing_token: token || "",
        login_token: loginToken,
        reason: declineReason.trim() || undefined,
      });
      setDeclined(true);
      setShowDeclineConfirm(false);
    } catch (error) {
      if (isSigningAuthError(error)) setAuthExpired(true);
      else toast.error(refusalMessage(error));
    }
  };

  const areInputFieldsValid = (
    values: Record<string, string> = inputValues
  ) => {
    if (!pageData?.input_fields || pageData.input_fields.length === 0)
      return true;
    return pageData.input_fields.every(
      (field: { field_name: string }) =>
        (values[field.field_name] || "").trim().length > 0
    );
  };

  const areChoiceQuestionsValid = (
    values: Record<string, boolean> = choiceValues
  ) => {
    if (!pageData?.choice_questions || pageData.choice_questions.length === 0)
      return true;
    return pageData.choice_questions.every(
      (question: { required: boolean; options: { variable: string }[] }) =>
        !question.required ||
        question.options.some((option) => !!values[option.variable])
    );
  };

  const isSignatureValid = () => {
    if (signatureMethod === "type") return typedName.trim().length > 0;
    if (signatureMethod === "draw") return hasDrawnSignature;
    if (signatureMethod === "upload") return !!uploadedFile;
    return false;
  };

  if (!loginToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center relative">
          <div className="absolute top-2 end-2 z-10">
            <LanguageSelector variant="compact" />
          </div>
          <HiOutlineBan className="mx-auto text-red-500 mb-4" size={56} />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {t("signing.authRequired.title")}
          </h2>
          <p className="text-sm text-gray-500">
            {t("signing.authRequired.message")}
          </p>
        </div>
      </div>
    );
  }

  if (isValidatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="xl" />
      </div>
    );
  }

  if (isTokenError || authExpired || isSigningAuthError(error)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center relative">
          <div className="absolute top-2 end-2 z-10">
            <LanguageSelector variant="compact" />
          </div>
          <HiOutlineBan className="mx-auto text-red-400 mb-4" size={56} />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {t("signing.authError.title")}
          </h2>
          <p className="text-sm text-gray-500">
            {t("signing.authError.message")}
          </p>
          <p className="text-sm font-semibold text-gray-700 mt-3">
            {t("signing.authError.linkInstruction")}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="xl" />
      </div>
    );
  }

  const terminalStatus =
    pageData && pageData.status && pageData.status !== "active"
      ? (pageData.status as "signed" | "declined" | "cancelled" | "expired")
      : null;

  if (terminalStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center">
          {terminalStatus === "signed" ? (
            <>
              <HiOutlineCheckCircle
                className="mx-auto text-green-500 mb-4"
                size={56}
              />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                {t("signing.success.title")}
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                {t("signing.success.message")}
              </p>
              {pageData.signed_document_url && (
                <a
                  href={pageData.signed_document_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={downloadLinkClass}
                >
                  <HiOutlineDownload size={18} />
                  {t("signing.success.download")}
                </a>
              )}
            </>
          ) : terminalStatus === "declined" ? (
            <>
              <HiOutlineXCircle
                className="mx-auto text-red-400 mb-4"
                size={56}
              />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                {t("signing.declined.title")}
              </h2>
              <p className="text-sm text-gray-500">
                {t("signing.declined.message")}
              </p>
            </>
          ) : terminalStatus === "cancelled" ? (
            <>
              <HiOutlineBan className="mx-auto text-gray-400 mb-4" size={56} />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                {t("signing.cancelled.title")}
              </h2>
              <p className="text-sm text-gray-500">
                {t("signing.cancelled.message")}
              </p>
            </>
          ) : (
            <>
              <HiOutlineClock
                className="mx-auto text-gray-400 mb-4"
                size={56}
              />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                {t("signing.expired.title")}
              </h2>
              <p className="text-sm text-gray-500">
                {t("signing.expired.message")}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (isError || !pageData) {
    const status = (error as any)?.response?.data?.element?.status;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center">
          {status === "expired" ? (
            <>
              <HiOutlineClock
                className="mx-auto text-gray-400 mb-4"
                size={48}
              />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                {t("signing.expired.title")}
              </h2>
              <p className="text-sm text-gray-500">
                {t("signing.expired.message")}
              </p>
            </>
          ) : status === "signed" ? (
            <>
              <HiOutlineCheckCircle
                className="mx-auto text-green-500 mb-4"
                size={48}
              />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                {t("signing.success.title")}
              </h2>
              <p className="text-sm text-gray-500">
                {t("signing.success.message")}
              </p>
            </>
          ) : status === "cancelled" ? (
            <>
              <HiOutlineBan className="mx-auto text-gray-400 mb-4" size={48} />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                {t("signing.cancelled.title")}
              </h2>
              <p className="text-sm text-gray-500">
                {t("signing.cancelled.message")}
              </p>
            </>
          ) : status === "declined" ? (
            <>
              <HiOutlineXCircle
                className="mx-auto text-red-400 mb-4"
                size={48}
              />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                {t("signing.declined.title")}
              </h2>
              <p className="text-sm text-gray-500">
                {t("signing.declined.message")}
              </p>
            </>
          ) : (
            <>
              <HiOutlineDocumentText
                className="mx-auto text-gray-400 mb-4"
                size={48}
              />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                {t("signing.linkInvalid.title")}
              </h2>
              <p className="text-sm text-gray-500">
                {t("signing.linkInvalid.message")}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center relative">
            <div className="absolute top-2 end-2 z-10">
              <LanguageSelector variant="compact" />
            </div>
            <HiOutlineCheckCircle
              className="mx-auto text-green-500 mb-4"
              size={56}
            />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {t("signing.success.title")}
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              {t("signing.success.message")}
            </p>
            {signedDocumentUrl && (
              <a
                href={signedDocumentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={downloadLinkClass}
              >
                <HiOutlineDownload size={18} />
                {t("signing.success.download")}
              </a>
            )}
          </div>
        </div>
        <footer className="py-4">
          <BusinessCardFooter showFooter={true} theme="modern" />
        </footer>
      </div>
    );
  }

  if (declined) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center relative">
            <div className="absolute top-2 end-2 z-10">
              <LanguageSelector variant="compact" />
            </div>
            <HiOutlineXCircle className="mx-auto text-red-400 mb-4" size={56} />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              {t("signing.declinedConfirm.title")}
            </h2>
            <p className="text-sm text-gray-500">
              {t("signing.declinedConfirm.message")}
            </p>
          </div>
        </div>
        <footer className="py-4">
          <BusinessCardFooter showFooter={true} theme="modern" />
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Helmet>
        <title>{`${t("signing.pageTitle")} - ${
          pageData.entity_name ?? ""
        }`}</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <header className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          {pageData.entity_logo && (
            <img
              src={pageData.entity_logo}
              alt={pageData.entity_name}
              className="h-8 w-8 rounded object-contain"
            />
          )}
          <span className="font-semibold text-gray-800 flex-1">
            {pageData.entity_name}
          </span>
          <LanguageSelector variant="compact" />
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-4 py-6 space-y-6 w-full">
        {pageData.message && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-600 italic">
              &ldquo;{pageData.message}&rdquo;
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <HiOutlineDocumentText className="text-blue-600" size={20} />
            <h2 className="font-medium text-gray-800">
              {pageData.template_name}
            </h2>
          </div>
          <p className="text-sm text-gray-500">
            {t("signing.greeting", { name: pageData.signer_name })}
          </p>
        </div>

        {currentStep === "fill" &&
          pageData.input_fields &&
          pageData.input_fields.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
              <h3 className="font-medium text-gray-800">
                {t("signing.inputFields")}
              </h3>
              {pageData.input_fields.map(
                (field: {
                  variable: string;
                  field_name: string;
                  value: string | null;
                }) => {
                  const label = field.field_name.replace(/^\w/, (c: string) =>
                    c.toUpperCase()
                  );

                  const currentValue = inputValues[field.field_name] || "";
                  const isEmpty = currentValue.trim().length === 0;
                  const showError = showValidationErrors && isEmpty;

                  return (
                    <div key={field.variable}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {label}
                        <span className="text-red-500 ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className={`w-full border rounded-md p-2.5 md:p-2 text-base md:text-sm focus:ring-blue-500 focus:border-blue-500 ${
                          showError
                            ? "border-red-400 bg-red-50"
                            : "border-gray-300"
                        }`}
                        value={currentValue}
                        maxLength={150}
                        onChange={(e) => {
                          const nextValue = sanitizeEsignatureInput(
                            e.target.value
                          );
                          setInputValues((prev) => {
                            const next = {
                              ...prev,
                              [field.field_name]: nextValue,
                            };
                            if (
                              showValidationErrors &&
                              areInputFieldsValid(next) &&
                              areChoiceQuestionsValid()
                            ) {
                              setShowValidationErrors(false);
                            }
                            return next;
                          });
                        }}
                        required
                      />
                      <div className="flex items-center justify-between mt-1">
                        {showError ? (
                          <p className="text-xs text-red-500">
                            {t("signing.fieldRequired")}
                          </p>
                        ) : (
                          <span />
                        )}
                        <p className="text-xs text-gray-400">
                          {currentValue.length}/150
                        </p>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          )}

        {currentStep === "fill" &&
          pageData.choice_questions &&
          pageData.choice_questions.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-5">
              {pageData.choice_questions.map(
                (
                  question: {
                    title_variable: string | null;
                    title: string;
                    required: boolean;
                    options: { variable: string; label: string }[];
                  },
                  index: number
                ) => {
                  const hasSelection = question.options.some(
                    (option) => !!choiceValues[option.variable]
                  );
                  const showError =
                    showValidationErrors && question.required && !hasSelection;

                  return (
                    <fieldset
                      key={question.title_variable || `question-${index}`}
                    >
                      <legend className="text-sm font-medium text-gray-800 mb-2">
                        {question.title || t("signing.choiceFieldsTitle")}
                        {question.required && (
                          <span className="text-red-500 ms-1">*</span>
                        )}
                      </legend>
                      <div className="space-y-2">
                        {question.options.map((option) => (
                          <label
                            key={option.variable}
                            className="flex items-start gap-3 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={!!choiceValues[option.variable]}
                              onChange={(e) => {
                                setChoiceValues((prev) => {
                                  const next = {
                                    ...prev,
                                    [option.variable]: e.target.checked,
                                  };
                                  if (
                                    showValidationErrors &&
                                    areInputFieldsValid() &&
                                    areChoiceQuestionsValid(next)
                                  ) {
                                    setShowValidationErrors(false);
                                  }
                                  return next;
                                });
                              }}
                              className={`mt-0.5 rounded focus:ring-blue-500 text-blue-600 ${
                                showError ? "border-red-400" : "border-gray-300"
                              }`}
                            />
                            <span className="text-sm text-gray-700">
                              {option.label}
                            </span>
                          </label>
                        ))}
                      </div>
                      {showError && (
                        <p className="text-xs text-red-500 mt-1">
                          {t("signing.choiceRequired")}
                        </p>
                      )}
                    </fieldset>
                  );
                }
              )}
            </div>
          )}

        {currentStep === "fill" && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <CustomButton
              variant="primary"
              size="lg"
              className="w-full"
              onClick={handlePreview}
              disabled={previewMutation.isLoading}
              title={
                previewMutation.isLoading
                  ? t("signing.previewLoading")
                  : t("signing.previewButton")
              }
              icon={
                previewMutation.isLoading ? <Spinner size="sm" /> : undefined
              }
            />

            {previewError && (
              <p className="text-xs text-red-600 mt-2 text-center" role="alert">
                {previewError}
              </p>
            )}

            <div className="text-center mt-3">
              <CustomButton
                variant="none"
                size="sm"
                className="mx-auto px-0 text-gray-400 hover:text-red-500"
                onClick={() => setShowDeclineConfirm(true)}
                title={t("signing.declineLink")}
              />
            </div>
          </div>
        )}

        {currentStep === "review" && previewUrl && (
          <div className="space-y-4">
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
              <h3 className="font-medium text-blue-800 mb-1">
                {t("signing.review.title")}
              </h3>
              <p className="text-sm text-blue-600">
                {t("signing.review.description")}
              </p>
            </div>

            <div
              className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden overflow-y-auto"
              style={{ maxHeight: "70vh" }}
            >
              <Suspense
                fallback={
                  <div className="flex flex-col items-center justify-center py-12">
                    <Spinner size="lg" />
                    <p className="mt-3 text-sm text-gray-500">
                      {t("signing.review.documentLoading")}
                    </p>
                  </div>
                }
              >
                <PdfDocumentViewer
                  url={previewUrl}
                  loadingLabel={t("signing.review.documentLoading") as string}
                  errorLabel={t("signing.review.documentError") as string}
                  fallbackLinkLabel={t("signing.review.openInNewTab") as string}
                />
              </Suspense>
            </div>

            <p className="text-xs text-gray-500 text-center">
              {t("signing.review.cantSeeDocument")}{" "}
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline font-medium"
              >
                {t("signing.review.openInNewTab")}
              </a>
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <CustomButton
                variant="custom"
                size="lg"
                className="flex-1 bg-green-600 font-medium text-white enabled:hover:bg-green-700 focus-visible:ring-green-500"
                onClick={handleApprove}
                title={t("signing.review.approveButton")}
              />
              <CustomButton
                variant="secondary"
                size="lg"
                className="flex-1 font-medium"
                onClick={() => {
                  setPreviewUrl(null);
                  setCurrentStep("fill");
                }}
                title={t("signing.review.backButton")}
              />
              <CustomButton
                variant="custom"
                size="lg"
                className="flex-1 border-red-300 bg-white font-medium text-red-600 enabled:hover:bg-red-50 focus-visible:ring-red-500"
                onClick={() => setShowDeclineConfirm(true)}
                title={t("signing.review.declineButton")}
              />
            </div>
          </div>
        )}

        {currentStep === "sign" && pageData.has_signature_field && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
            <h3 className="font-medium text-gray-800">
              {t("signing.signatureSection")}
            </h3>

            {signatureMethod === "type" && (
              <div>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md p-2.5 md:p-2 text-base md:text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t("signing.typePlaceholder") as string}
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  maxLength={35}
                />
                <CharacterCounter
                  currentLength={typedName.length}
                  maxLength={35}
                />
                {typedName.trim() && (
                  <div className="mt-3 border border-gray-200 rounded-lg p-4 bg-gray-50 text-center">
                    <span
                      className="text-gray-900"
                      style={{
                        fontFamily: "'Dancing Script', cursive",
                        fontSize: "28px",
                      }}
                    >
                      {typedName}
                    </span>
                  </div>
                )}
                <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
                  <button
                    className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                    onClick={() => setSignatureMethod("draw")}
                  >
                    <HiOutlinePencil size={14} />
                    {t("signing.methods.drawInstead")}
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                    onClick={() => setSignatureMethod("upload")}
                  >
                    <HiOutlineUpload size={14} />
                    {t("signing.methods.uploadInstead")}
                  </button>
                </div>
              </div>
            )}

            {signatureMethod === "draw" && (
              <div>
                <canvas
                  ref={canvasRef}
                  className="w-full border border-gray-300 rounded-md bg-white cursor-crosshair"
                  style={{ height: "150px", touchAction: "none" }}
                />
                <div className="mt-2 flex items-center justify-between">
                  <button
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => {
                      signaturePadRef.current?.clear();
                      setHasDrawnSignature(false);
                    }}
                  >
                    {t("signing.clearSignature")}
                  </button>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <button
                      className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                      onClick={() => setSignatureMethod("type")}
                    >
                      <HiOutlineDocumentText size={14} />
                      {t("signing.methods.typeInstead")}
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                      onClick={() => setSignatureMethod("upload")}
                    >
                      <HiOutlineUpload size={14} />
                      {t("signing.methods.uploadInstead")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {signatureMethod === "upload" && (
              <div>
                {uploadPreview ? (
                  <div className="relative">
                    <img
                      src={uploadPreview}
                      alt="Signature"
                      className="max-h-32 mx-auto border border-gray-200 rounded-lg"
                    />
                    <button
                      className="mt-2 text-xs text-blue-600 hover:underline block mx-auto"
                      onClick={() => {
                        setUploadedFile(null);
                        setUploadPreview("");
                        setUploadError("");
                      }}
                    >
                      {t("signing.removeUpload")}
                    </button>
                  </div>
                ) : (
                  <div>
                    <label
                      className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
                        uploadError
                          ? "border-red-400 hover:border-red-500"
                          : "border-gray-300 hover:border-blue-400"
                      }`}
                    >
                      <HiOutlinePhotograph
                        className="text-gray-400 mb-2"
                        size={32}
                      />
                      <span className="text-xs text-gray-500">
                        {t("signing.uploadHint")}
                      </span>
                      <span className="text-xs text-gray-400 mt-1">
                        {t("signing.upload.acceptedFormats")}
                      </span>
                      <input
                        type="file"
                        accept={ACCEPTED_SIGNATURE_IMAGE_EXTENSIONS}
                        className="hidden"
                        onChange={handleUploadChange}
                      />
                    </label>
                    {uploadError && (
                      <p
                        className="text-xs text-red-500 mt-1 text-center"
                        role="alert"
                      >
                        {uploadError}
                      </p>
                    )}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
                  <button
                    className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                    onClick={() => setSignatureMethod("type")}
                  >
                    <HiOutlineDocumentText size={14} />
                    {t("signing.methods.typeInstead")}
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                    onClick={() => setSignatureMethod("draw")}
                  >
                    <HiOutlinePencil size={14} />
                    {t("signing.methods.drawInstead")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === "sign" && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-600">
                {t("signing.consentText")}
              </span>
            </label>

            {submitError && (
              <p className="text-xs text-red-600 text-center" role="alert">
                {submitError}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              {submitError && (
                <CustomButton
                  variant="secondary"
                  size="lg"
                  className="font-medium"
                  title={t("signing.restartButton")}
                  onClick={() => {
                    setSubmitError("");
                    setPreviewUrl(null);
                    setPreviewError("");
                    setTypedName("");
                    setUploadedFile(null);
                    setUploadPreview("");
                    setUploadError("");
                    setHasDrawnSignature(false);
                    setCurrentStep("fill");
                  }}
                  disabled={submitMutation.isLoading}
                />
              )}
              <CustomButton
                variant="primary"
                size="lg"
                className="flex-1 font-medium"
                onClick={handleSubmit}
                isLoading={submitMutation.isLoading}
                disabled={
                  !consent ||
                  (pageData.has_signature_field && !isSignatureValid()) ||
                  submitMutation.isLoading
                }
                title={t("signing.signButton")}
              />
            </div>
          </div>
        )}

        {showDeclineConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {t("signing.declineConfirm.title")}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {t("signing.declineConfirm.message")}
              </p>
              <div className="mb-4">
                <textarea
                  className="w-full border border-gray-300 rounded-md p-2.5 md:p-2 text-base md:text-sm text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-300"
                  rows={3}
                  maxLength={200}
                  placeholder={
                    t("signing.declineConfirm.reasonPlaceholder") || ""
                  }
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                />
                <CharacterCounter
                  currentLength={declineReason.length}
                  maxLength={200}
                />
              </div>
              <div className="flex gap-3">
                <CustomButton
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setShowDeclineConfirm(false);
                    setDeclineReason("");
                  }}
                  title={t("signing.declineConfirm.cancel")}
                />
                <CustomButton
                  variant="danger"
                  className="flex-1"
                  onClick={handleDecline}
                  isLoading={declineMutation.isLoading}
                  disabled={declineMutation.isLoading}
                  title={t("signing.declineConfirm.confirm")}
                />
              </div>
            </div>
          </div>
        )}

        <PhoneValidationModal
          namespace="clientView"
          show={showPhoneVerification}
          onClose={() => {
            closePhoneVerification();
            setSubmitError("");
          }}
          onConfirm={handlePhoneVerificationConfirm}
          onResend={handlePhoneVerificationResend}
          expiresAt={phoneVerificationExpiresAt}
          codeSpent={codeSpent}
          loading={phoneVerificationLoading}
          phoneNumber={phoneVerificationPhone}
        />
      </main>

      <footer className="mt-auto py-4">
        <BusinessCardFooter showFooter={true} theme="modern" />
      </footer>
    </div>
  );
};

export default SigningPage;
